import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import {
  MODUL_LABELS,
  MODUL_ORDER,
  MODUL_SAISON,
  MODUL_TEMPLATES,
  type BvModulId,
  type Frequenz,
  type FrequenzPeriod,
} from '../lib/bv-modul-templates';
import { POSITIONEN_KATALOG } from '../lib/bv-positions-katalog';
import {
  berechneVorschlagFuerModul,
  PRICING_DEFAULT,
  type ObjektPricingInput,
  type PricingShape,
  type ReinigungProWocheKey,
} from '../lib/bv-auto-pricing';
import {
  buildBeschreibungText,
  computeModulPricing,
  defaultPositionsForModul,
  defaultSaisonMonateForModul,
  emptyModulState,
  FREITEXT_DEFAULT_FREQUENZ,
  ModulDetailCard,
  periodHatCount,
  type ModulFormState,
  type Objekt,
} from '../components/vertraege/ModulDetailCard';

// Alle 7 Module sind jetzt im Wizard verfügbar.
const SUPPORTED_MODULE: ReadonlySet<BvModulId> = new Set<BvModulId>([
  'haustechnik',
  'innenreinigung',
  'aussenreinigung',
  'bereitschaftsdienst',
  'sonderarbeiten',
  'gruenflaechenpflege',
  'winterdienst',
]);

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

type Step = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<Step, string> = {
  1: 'Stammdaten',
  2: 'Module',
  3: 'Detail',
  4: 'Zusammenfassung',
};

export const Route = createFileRoute('/vertraege/neu_/vorlagen')({
  beforeLoad: () => requireAccess('/api/v1/vertraege/entwuerfe'),
  component: VorlagenWizardPage,
});

function makeObjektPricingInput(o: Objekt | undefined): Partial<ObjektPricingInput> {
  if (!o) return {};
  const reinigung = o.reinigungProWoche;
  // Drizzle gibt numeric als String zurück (z.B. "2.00"); auf Key normalisieren.
  let reinigungKey: ReinigungProWocheKey | null = null;
  if (reinigung != null) {
    const n = Number(reinigung);
    const candidate = (n === 0.5 ? '0.5' : String(n)) as ReinigungProWocheKey;
    if (
      candidate === '0.5' ||
      candidate === '1' ||
      candidate === '2' ||
      candidate === '3' ||
      candidate === '4' ||
      candidate === '5' ||
      candidate === '6' ||
      candidate === '7'
    ) {
      reinigungKey = candidate;
    }
  }
  return {
    anzahlEingaenge: o.anzahlEingaenge,
    hausGroesseKlasse: o.hausGroesseKlasse,
    anzahlEinheiten: o.anzahlEinheiten,
    flurgroesseKlasse: o.flurgroesseKlasse,
    reinigungProWoche: reinigungKey,
    aussenflaecheQm: o.aussenflaecheQm != null ? Number(o.aussenflaecheQm) : null,
    gruenflaecheQm: o.gruenflaecheQm != null ? Number(o.gruenflaecheQm) : null,
    winterdienstflaecheQm: o.winterdienstflaecheQm != null ? Number(o.winterdienstflaecheQm) : null,
  };
}

export function VorlagenWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [modulIds, setModulIds] = useState<Set<BvModulId>>(new Set());
  const [perModul, setPerModul] = useState<Record<string, ModulFormState>>({});
  const [error, setError] = useState<Error | null>(null);

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });
  const pricingQ = useQuery({
    queryKey: ['vertraege', 'pricing-effective'],
    queryFn: () => api<{ pricing: PricingShape }>('/api/v1/vertraege/pricing-effective'),
  });

  const objekt = (objekteQ.data?.items ?? []).find((o) => o.id === objektId);
  const objekteForKunde = (objekteQ.data?.items ?? []).filter(
    (o) => kundeId === '' || o.kundeId === kundeId,
  );

  const objektPricingInput = useMemo(() => makeObjektPricingInput(objekt), [objekt]);
  const pricing = pricingQ.data?.pricing ?? PRICING_DEFAULT;

  const createMut = useMutation({
    mutationFn: (body: unknown) =>
      api<{ entwurf: { id: string } }>('/api/v1/vertraege/entwuerfe', {
        method: 'POST',
        body,
      }),
    onSuccess: (r) => {
      void navigate({ to: '/vertraege/entwurf/$id', params: { id: r.entwurf.id } });
    },
    onError: (e: unknown) => {
      if (e instanceof Error) setError(e);
      else if (e instanceof ApiError) setError(new Error(e.message));
      else setError(new Error(String(e)));
    },
  });

  // Init perModul-State, sobald ein Modul neu in Schritt 3 aktiviert wird.
  useEffect(() => {
    if (!objekt) return;
    setPerModul((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of modulIds) {
        if (!next[id]?.initialized) {
          const defaults = defaultPositionsForModul(id, objekt.hatAufzug);
          next[id] = {
            selectedPositionIds: defaults.ids,
            positionFrequenzen: defaults.frequenzen,
            freitextItems: [],
            pauschalMonatsbetrag: '',
            einsaetzeProJahr: '',
            einsatzPreis: '',
            bereitschaftProMonat: '',
            saisonMonate: defaultSaisonMonateForModul(id),
            initialized: true,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [modulIds, objekt]);

  function toggleModul(id: BvModulId): void {
    if (!SUPPORTED_MODULE.has(id)) return;
    setModulIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function updateModul(id: BvModulId, patch: Partial<ModulFormState>): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      return { ...prev, [id]: { ...cur, ...patch, initialized: true } };
    });
  }

  function togglePosition(id: BvModulId, positionId: string): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      const nextSelected = new Set(cur.selectedPositionIds);
      const nextFrequenzen = { ...cur.positionFrequenzen };
      if (nextSelected.has(positionId)) {
        nextSelected.delete(positionId);
      } else {
        nextSelected.add(positionId);
        if (!nextFrequenzen[positionId]) {
          const katalogEintrag = POSITIONEN_KATALOG[id].find((p) => p.id === positionId);
          if (katalogEintrag) nextFrequenzen[positionId] = { ...katalogEintrag.frequenz };
        }
      }
      return {
        ...prev,
        [id]: {
          ...cur,
          selectedPositionIds: nextSelected,
          positionFrequenzen: nextFrequenzen,
          initialized: true,
        },
      };
    });
  }

  function setPositionFrequenz(id: BvModulId, positionId: string, patch: Partial<Frequenz>): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      const katalogDefault = POSITIONEN_KATALOG[id].find((p) => p.id === positionId)?.frequenz;
      const current = cur.positionFrequenzen[positionId] ??
        katalogDefault ?? { count: 1, period: 'monthly' as FrequenzPeriod };
      const merged: Frequenz = { ...current, ...patch };
      if (!periodHatCount(merged.period)) merged.count = 1;
      return {
        ...prev,
        [id]: {
          ...cur,
          positionFrequenzen: { ...cur.positionFrequenzen, [positionId]: merged },
          initialized: true,
        },
      };
    });
  }

  function addFreitext(id: BvModulId): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      return {
        ...prev,
        [id]: {
          ...cur,
          freitextItems: [
            ...cur.freitextItems,
            { text: '', frequenz: { ...FREITEXT_DEFAULT_FREQUENZ } },
          ],
          initialized: true,
        },
      };
    });
  }

  function updateFreitextText(id: BvModulId, idx: number, value: string): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      const next = cur.freitextItems.slice();
      const target = next[idx];
      if (!target) return prev;
      next[idx] = { ...target, text: value };
      return { ...prev, [id]: { ...cur, freitextItems: next, initialized: true } };
    });
  }

  function setFreitextFrequenz(id: BvModulId, idx: number, patch: Partial<Frequenz>): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      const next = cur.freitextItems.slice();
      const target = next[idx];
      if (!target) return prev;
      const merged: Frequenz = { ...target.frequenz, ...patch };
      if (!periodHatCount(merged.period)) merged.count = 1;
      next[idx] = { ...target, frequenz: merged };
      return { ...prev, [id]: { ...cur, freitextItems: next, initialized: true } };
    });
  }

  function removeFreitext(id: BvModulId, idx: number): void {
    setPerModul((prev) => {
      const cur = prev[id] ?? emptyModulState();
      const next = cur.freitextItems.filter((_, i) => i !== idx);
      return { ...prev, [id]: { ...cur, freitextItems: next, initialized: true } };
    });
  }

  function applyAutoPricing(id: BvModulId): void {
    const v = berechneVorschlagFuerModul(id, objektPricingInput, pricing);
    if (!v.kannBerechnetWerden) return;
    const tpl = MODUL_TEMPLATES[id];
    if (tpl.defaultKalkulationstyp === 'saisonal_verteilt') {
      const einsaetze = Number(perModul[id]?.einsaetzeProJahr);
      if (!Number.isFinite(einsaetze) || einsaetze <= 0) return;
      const preis = (v.vorschlagBetrag * 12) / einsaetze;
      updateModul(id, { einsatzPreis: preis.toFixed(2) });
      return;
    }
    if (tpl.defaultKalkulationstyp === 'bereitschaft_plus_einsatz') {
      const saisonCount = perModul[id]?.saisonMonate.length ?? 0;
      if (saisonCount === 0) return;
      const bereitschaft = (v.vorschlagBetrag * 12) / saisonCount;
      updateModul(id, { bereitschaftProMonat: bereitschaft.toFixed(2) });
      return;
    }
    updateModul(id, { pauschalMonatsbetrag: v.vorschlagBetrag.toFixed(2) });
  }

  function handleNext(): void {
    if (step === 1) {
      if (!kundeId || !objektId) {
        setError(new Error('Bitte Kunde und Objekt wählen.'));
        return;
      }
      setError(null);
      setStep(2);
      return;
    }
    if (step === 2) {
      if (modulIds.size === 0) {
        setError(new Error('Mindestens ein Modul auswählen.'));
        return;
      }
      setError(null);
      setStep(3);
      return;
    }
    if (step === 3) {
      // Pricing pro Modul muss valide sein. computeModulPricing kennt den
      // Kalkulationstyp und prüft die jeweils relevanten Felder.
      const fehlend: string[] = [];
      for (const id of modulIds) {
        const state = perModul[id] ?? emptyModulState();
        const pricing = computeModulPricing(id, state);
        if (!pricing.valid) {
          fehlend.push(MODUL_LABELS[id]);
        }
      }
      if (fehlend.length > 0) {
        setError(new Error(`Preis-Eingabe fehlt für: ${fehlend.join(', ')}`));
        return;
      }
      setError(null);
      setStep(4);
      return;
    }
  }

  function handleBack(): void {
    setError(null);
    if (step === 1) {
      void navigate({ to: '/vertraege' });
      return;
    }
    setStep((s) => (s - 1) as Step);
  }

  function handleSubmit(): void {
    if (!objekt) {
      setError(new Error('Objekt nicht geladen.'));
      return;
    }
    const moduleSorted = MODUL_ORDER.filter((id) => modulIds.has(id));
    const bausteine = moduleSorted.map((id, idx) => {
      const tpl = MODUL_TEMPLATES[id];
      const state = perModul[id] ?? emptyModulState();
      const beschreibungText = buildBeschreibungText(
        id,
        state.selectedPositionIds,
        state.positionFrequenzen,
        state.freitextItems,
      );
      const pricing = computeModulPricing(id, state);
      const baustein: Record<string, unknown> = {
        position: idx + 1,
        bezeichnung: tpl.defaultBausteinBezeichnung,
        kalkulationstyp: tpl.defaultKalkulationstyp,
        monatsbetragNetto: pricing.monatsbetragNetto,
        jahresbetragNetto: pricing.jahresbetragNetto,
        modulId: id,
      };
      if (tpl.defaultKalkulationstyp === 'saisonal_verteilt') {
        baustein['einsaetzeProJahr'] = Number(state.einsaetzeProJahr);
        baustein['einsatzPreis'] = Number(state.einsatzPreis).toFixed(2);
      } else if (tpl.defaultKalkulationstyp === 'bereitschaft_plus_einsatz') {
        baustein['bereitschaftProMonat'] = Number(state.bereitschaftProMonat).toFixed(2);
        baustein['saisonMonate'] = state.saisonMonate;
      } else {
        baustein['pauschalMonatsbetrag'] = Number(state.pauschalMonatsbetrag).toFixed(2);
      }
      if (beschreibungText.length > 0) baustein['beschreibungText'] = beschreibungText;
      return baustein;
    });

    const body = {
      kundeId,
      objektId,
      initialeVersion: { bausteine },
    };
    createMut.mutate(body);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Neuer Vertragsentwurf aus Vorlagen"
        description="In 4 Schritten: Kunde+Objekt → Module wählen → Detail je Modul → Zusammenfassung. Beginn und Vertragsname werden erst beim Aktivieren des unterschriebenen Vertrags gesetzt."
      />

      <Stepper current={step} />

      {error ? <ErrorBanner error={error} /> : null}

      {step === 1 ? (
        <StepStammdaten
          kundeId={kundeId}
          setKundeId={(id) => {
            setKundeId(id);
            setObjektId('');
          }}
          objektId={objektId}
          setObjektId={setObjektId}
          kunden={kundenQ.data?.items ?? []}
          objekteForKunde={objekteForKunde}
        />
      ) : null}

      {step === 2 ? <StepModulAuswahl modulIds={modulIds} toggleModul={toggleModul} /> : null}

      {step === 3 && objekt ? (
        <StepModulDetail
          modulIds={modulIds}
          objekt={objekt}
          objektPricingInput={objektPricingInput}
          pricing={pricing}
          perModul={perModul}
          onTogglePosition={togglePosition}
          onSetPositionFrequenz={setPositionFrequenz}
          onAddFreitext={addFreitext}
          onUpdateFreitextText={updateFreitextText}
          onSetFreitextFrequenz={setFreitextFrequenz}
          onRemoveFreitext={removeFreitext}
          onApplyAutoPricing={applyAutoPricing}
          onChangePauschal={(id, value) => updateModul(id, { pauschalMonatsbetrag: value })}
          onChangeEinsaetzeProJahr={(id, value) => updateModul(id, { einsaetzeProJahr: value })}
          onChangeEinsatzPreis={(id, value) => updateModul(id, { einsatzPreis: value })}
          onChangeBereitschaft={(id, value) => updateModul(id, { bereitschaftProMonat: value })}
          onChangeSaisonMonate={(id, months) => updateModul(id, { saisonMonate: months })}
        />
      ) : null}

      {step === 4 && objekt ? (
        <StepZusammenfassung
          objekt={objekt}
          modulIds={modulIds}
          perModul={perModul}
          submitting={createMut.isPending}
          onSubmit={handleSubmit}
        />
      ) : null}

      <div className="flex justify-between">
        <Button variant="ghost" onClick={handleBack} disabled={createMut.isPending}>
          {step === 1 ? 'Abbrechen' : 'Zurück'}
        </Button>
        {step < 4 ? <Button onClick={handleNext}>Weiter</Button> : null}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const steps: Step[] = [1, 2, 3, 4];
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label="Schritte">
      {steps.map((s, idx) => {
        const isCurrent = s === current;
        const isDone = s < current;
        const cls = isCurrent
          ? 'border-brand-600 bg-brand-50 text-brand-700'
          : isDone
            ? 'border-green-300 bg-green-50 text-green-700'
            : 'border-slate-200 bg-white text-slate-500';
        return (
          <li key={s} className="flex items-center gap-2">
            <span
              data-testid={`step-${s}`}
              aria-current={isCurrent ? 'step' : undefined}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 font-medium ${cls}`}
            >
              <span className="font-mono">{s}.</span> {STEP_LABELS[s]}
            </span>
            {idx < steps.length - 1 ? <span className="text-slate-300">→</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

function StepStammdaten(props: {
  kundeId: string;
  setKundeId: (v: string) => void;
  objektId: string;
  setObjektId: (v: string) => void;
  kunden: Kunde[];
  objekteForKunde: Objekt[];
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-700" htmlFor="kunde">
            Kunde *
          </label>
          <Select
            id="kunde"
            value={props.kundeId}
            onChange={(e) => props.setKundeId(e.target.value)}
          >
            <option value="">— wählen —</option>
            {props.kunden.map((k) => (
              <option key={k.id} value={k.id}>
                {k.firmenname} ({k.kundennummer})
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700" htmlFor="objekt">
            Objekt *
          </label>
          <Select
            id="objekt"
            value={props.objektId}
            onChange={(e) => props.setObjektId(e.target.value)}
            disabled={!props.kundeId}
          >
            <option value="">— wählen —</option>
            {props.objekteForKunde.map((o) => (
              <option key={o.id} value={o.id}>
                {o.bezeichnung}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Beginn und Vertragsname werden erst beim Aktivieren (Upload des unterschriebenen
        Vertrags-PDF) gesetzt.
      </p>
    </div>
  );
}

function StepModulAuswahl({
  modulIds,
  toggleModul,
}: {
  modulIds: Set<BvModulId>;
  toggleModul: (id: BvModulId) => void;
}) {
  const cards = useMemo(
    () =>
      MODUL_ORDER.map((id) => {
        const positions = POSITIONEN_KATALOG[id];
        const defaultCount = positions.filter((p) => p.defaultAktiv).length;
        const saison = MODUL_SAISON[id];
        const intro = MODUL_TEMPLATES[id].defaultBeschreibungIntro ?? '';
        return { id, defaultCount, saison, intro, supported: SUPPORTED_MODULE.has(id) };
      }),
    [],
  );

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((c) => {
        const active = modulIds.has(c.id);
        const cls = !c.supported
          ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          : active
            ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
            : 'border-slate-200 bg-white hover:border-slate-300';
        return (
          <button
            key={c.id}
            type="button"
            role="checkbox"
            aria-checked={active}
            aria-disabled={!c.supported}
            data-testid={`modul-card-${c.id}`}
            disabled={!c.supported}
            onClick={() => toggleModul(c.id)}
            className={`rounded-lg border p-4 text-left shadow-sm transition ${cls}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">{MODUL_LABELS[c.id]}</span>
              <span
                className={`inline-block h-4 w-4 rounded border ${
                  active ? 'border-brand-600 bg-brand-600' : 'border-slate-300 bg-white'
                }`}
                aria-hidden="true"
              />
            </div>
            <p className="mt-2 text-xs">
              {c.defaultCount > 0
                ? `${c.defaultCount} Default-Positionen`
                : 'Positionen frei eintragen'}
            </p>
            {c.saison ? <p className="mt-1 text-xs text-slate-500">Saison: {c.saison}</p> : null}
            {c.intro ? <p className="mt-2 text-xs text-slate-500 line-clamp-2">{c.intro}</p> : null}
            {!c.supported ? (
              <p className="mt-2 text-xs italic text-slate-500">Detail-Karte folgt in 6a-3</p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function StepModulDetail(props: {
  modulIds: Set<BvModulId>;
  objekt: Objekt;
  objektPricingInput: Partial<ObjektPricingInput>;
  pricing: PricingShape;
  perModul: Record<string, ModulFormState>;
  onTogglePosition: (id: BvModulId, positionId: string) => void;
  onSetPositionFrequenz: (id: BvModulId, positionId: string, patch: Partial<Frequenz>) => void;
  onAddFreitext: (id: BvModulId) => void;
  onUpdateFreitextText: (id: BvModulId, idx: number, value: string) => void;
  onSetFreitextFrequenz: (id: BvModulId, idx: number, patch: Partial<Frequenz>) => void;
  onRemoveFreitext: (id: BvModulId, idx: number) => void;
  onApplyAutoPricing: (id: BvModulId) => void;
  onChangePauschal: (id: BvModulId, value: string) => void;
  onChangeEinsaetzeProJahr: (id: BvModulId, value: string) => void;
  onChangeEinsatzPreis: (id: BvModulId, value: string) => void;
  onChangeBereitschaft: (id: BvModulId, value: string) => void;
  onChangeSaisonMonate: (id: BvModulId, months: string[]) => void;
}) {
  const modulOrder = MODUL_ORDER.filter((id) => props.modulIds.has(id));
  return (
    <div className="space-y-4">
      {modulOrder.map((id) => (
        <ModulDetailCard
          key={id}
          modulId={id}
          objekt={props.objekt}
          objektPricingInput={props.objektPricingInput}
          pricing={props.pricing}
          state={props.perModul[id] ?? emptyModulState()}
          onTogglePosition={(positionId) => props.onTogglePosition(id, positionId)}
          onSetPositionFrequenz={(positionId, patch) =>
            props.onSetPositionFrequenz(id, positionId, patch)
          }
          onAddFreitext={() => props.onAddFreitext(id)}
          onUpdateFreitextText={(idx, v) => props.onUpdateFreitextText(id, idx, v)}
          onSetFreitextFrequenz={(idx, patch) => props.onSetFreitextFrequenz(id, idx, patch)}
          onRemoveFreitext={(idx) => props.onRemoveFreitext(id, idx)}
          onApplyAutoPricing={() => props.onApplyAutoPricing(id)}
          onChangePauschal={(v) => props.onChangePauschal(id, v)}
          onChangeEinsaetzeProJahr={(v) => props.onChangeEinsaetzeProJahr(id, v)}
          onChangeEinsatzPreis={(v) => props.onChangeEinsatzPreis(id, v)}
          onChangeBereitschaft={(v) => props.onChangeBereitschaft(id, v)}
          onChangeSaisonMonate={(months) => props.onChangeSaisonMonate(id, months)}
        />
      ))}
    </div>
  );
}

function StepZusammenfassung(props: {
  objekt: Objekt;
  modulIds: Set<BvModulId>;
  perModul: Record<string, ModulFormState>;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const moduleSorted = MODUL_ORDER.filter((id) => props.modulIds.has(id));
  const total = moduleSorted.reduce((sum, id) => {
    const n = Number(props.perModul[id]?.pauschalMonatsbetrag ?? '0');
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="step4">
      <h2 className="text-sm font-semibold text-slate-800">Zusammenfassung</h2>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-slate-500">Objekt</dt>
        <dd className="text-slate-800">{props.objekt.bezeichnung}</dd>
      </dl>
      <p className="mt-1 text-xs text-slate-500">
        Beginn und Vertragsname werden beim Aktivieren des unterschriebenen Vertrags gesetzt.
      </p>

      <ul className="mt-4 space-y-2">
        {moduleSorted.map((id) => {
          const state = props.perModul[id] ?? emptyModulState();
          const tpl = MODUL_TEMPLATES[id];
          const positionCount =
            state.selectedPositionIds.size +
            state.freitextItems.filter((item) => item.text.trim().length > 0).length;
          return (
            <li
              key={id}
              data-testid={`zusammenfassung-${id}`}
              className="rounded-md border border-slate-200 bg-slate-50 p-3"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-800">{tpl.defaultBausteinBezeichnung}</span>
                <span className="font-mono text-slate-700">
                  {Number(state.pauschalMonatsbetrag || '0').toFixed(2)} €/Monat
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {positionCount} Positionen · Kalkulation: {tpl.defaultKalkulationstyp}
              </p>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
        <span className="font-medium text-slate-800">Summe</span>
        <span className="font-mono text-slate-800" data-testid="summe">
          {total.toFixed(2)} €/Monat
        </span>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={props.onSubmit} disabled={props.submitting} data-testid="submit-entwurf">
          {props.submitting ? 'Lege an…' : 'Entwurf anlegen'}
        </Button>
      </div>
    </div>
  );
}
